import type { AppThunk, RootState } from "~/app/store";
import { queryClient } from "~/app/queryClient";
import { useQueries, useQuery, type UseQueryResult } from "@tanstack/react-query";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo } from "react";
import { addBaseProductDataAction } from "~/domains/product/redux/actions";
import { fetchProductInfo } from "~/domains/product/api/fetchProductInfo";
import { selectProductData } from "~/domains/product/redux/selectors";
import { buildProductDescriptor } from "~/domains/pna/descriptor";
import { loadProductPna } from "~/domains/pna/pnaCommands";
import {
  pnaAvailabilityQueryOptions,
  pnaPriceQueryOptions,
  type AvailabilityQueryResult,
} from "~/domains/pna/pnaQueries";
import { toAvailabilityOutcomeFromQuery, toOutcomeFromQuery } from "~/domains/pna/pnaSnapshot";
import type { AvailabilityInfo } from "~/domains/product/api/pna/AvailabilityInfo";
import {
  isKITProduct,
  ProductType,
  type PackagingInformationEntry,
  type ProductData,
  type WarehouseStockData,
} from "~/domains/product/types/ProductData";
import type { PriceInfo } from "~/domains/pricing/api/PriceInfo";
import { INVALID_PACKAGE_CODE } from "~/utils/constants";
import { toMapFn } from "~/common/utils/reducers";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";

/**
 * Product descriptors through the canonical PnA queries (task 0042, A05 slice 3).
 *
 * Hooks subscribe to `pnaPriceQueryOptions`/`pnaAvailabilityQueryOptions` and project through
 * `buildProductDescriptor` — they never read the legacy PnA Redux dictionaries. Basic product
 * data stays a Redux concern: the hooks still ensure `products.basic[code]` is staged (fetching
 * through `fetchProductInfo` + `addBaseProductDataAction`) exactly like the legacy
 * `requestFullProductData` thunk did.
 */

/** Legacy `requestFullProductData` normalized the `"-"` empty price-reference option to `""` before transporting. */
function normalizePriceReference(priceReference: string) {
  return priceReference === "-" ? "" : priceReference;
}

/** Legacy basic-data gate: fetch when absent, or when forced and the staged entry is not a full product. */
function needsBasicFetch(product: ProductData | undefined, forceFetchBasicData?: boolean) {
  return !product || (forceFetchBasicData && !product?.isFullProduct);
}

function pnaRequestProductCode(base: ProductData | undefined, storefrontCode: string) {
  return base?.type === ProductType.CONFIGURATOR ? base.hybrisCode : base?.code || storefrontCode;
}

/**
 * Stages the basic product in Redux when missing (or forced and partial), returning the staged
 * base — the basic-data half of the legacy `requestFullProductData` thunk.
 */
function ensureBaseProductData(productCode: string, forceFetchBasicData?: boolean): AppThunk<Promise<ProductData>> {
  return async (dispatch, getState) => {
    const existing = getState().products.basic[productCode];
    if (!needsBasicFetch(existing, forceFetchBasicData)) {
      return existing;
    }
    const product = await fetchProductInfo(productCode);
    dispatch(addBaseProductDataAction(product, forceFetchBasicData));
    return getState().products.basic[productCode] ?? product;
  };
}

/**
 * Loads one product's PnA for the imperative descriptor path through the canonical query cache,
 * honoring the legacy per-side skip flags: a skipped side resolves the loaded-empty value
 * (`[]` / `undefined`) without transport, like the legacy thunk's `Promise.resolve` fallbacks.
 * The default both-sides case goes through {@link loadProductPna}, which has no skip flags.
 * Regular and configurator products share this path; `configId` selects the linear transport.
 */
async function loadDescriptorPna(
  base: ProductData,
  priceReference: string,
  { fetchPrice = true, fetchAvailability = true }: { fetchPrice?: boolean; fetchAvailability?: boolean },
): Promise<{ price: PriceInfo[]; availability: AvailabilityInfo | undefined }> {
  const input = {
    productCode: pnaRequestProductCode(base, base.code),
    priceReference,
    configId: base.type === ProductType.CONFIGURATOR ? base.configId : undefined,
    priceEnabled: fetchPrice,
    availabilityEnabled: fetchAvailability,
  };
  return loadProductPna(queryClient, input);
}

/**
 * Projects the canonical PnA query results into a descriptor. `priceEnabled`/`availabilityEnabled`
 * must mirror the query-side gates (basic product staged, regular product type, side not skipped,
 * permission present): a query gated off projects `disabled` — the legacy loaded-empty shape.
 */
function projectDescriptorPna(
  base: ProductData,
  priceQuery: UseQueryResult<PriceInfo[], Error>,
  availabilityQuery: UseQueryResult<AvailabilityQueryResult, Error>,
  priceReference: string,
  priceEnabled: boolean,
  availabilityEnabled: boolean,
): ProductDescriptor {
  return buildProductDescriptor(
    base,
    {
      price: toOutcomeFromQuery(priceQuery, priceEnabled),
      availability: toAvailabilityOutcomeFromQuery(availabilityQuery, availabilityEnabled),
    },
    priceReference,
  );
}

/**
 * Price query options for one descriptor side. The canonical builder owns permission and the
 * caller's fetch applicability gate. An unstaged base queries as a regular product by code; its
 * descriptor lacks base data until the basic fetch lands.
 */
function descriptorPriceOptions(
  base: ProductData | undefined,
  productCode: string,
  priceReference: string,
  fetchPrice: boolean,
) {
  return pnaPriceQueryOptions({
    productCode: pnaRequestProductCode(base, productCode),
    priceReference,
    configId: base?.type === ProductType.CONFIGURATOR ? base.configId : undefined,
    enabled: fetchPrice,
  });
}

/** Availability query for one descriptor side — see {@link descriptorPriceOptions}. */
function descriptorAvailabilityOptions(base: ProductData | undefined, productCode: string, fetchAvailability: boolean) {
  return pnaAvailabilityQueryOptions({
    productCode: pnaRequestProductCode(base, productCode),
    configId: base?.type === ProductType.CONFIGURATOR ? base.configId : undefined,
    enabled: fetchAvailability,
  });
}

/**
 * Build a thunk that retrieves the current product descriptor and can optionally request fresh data.
 *
 * `current` is a synchronous snapshot WITHOUT transport (base read from `products.basic`; a
 * fetching side shows the loading state, a skipped/permission-missing side the loaded-empty
 * projection). `promise` resolves a descriptor built from the values returned by the canonical
 * PnA queries — no Redux PnA reread.
 *
 * Components should prefer the useProductDescriptor hook. Imperative callers must dispatch this thunk so
 * store access remains explicit and testable.
 */

function getProductDescriptor(
  productCode: string,
  priceReference: string,
): AppThunk<{
  current: ProductDescriptor;
  promise: undefined;
}>;
function getProductDescriptor(
  productCode: string,
  priceReference: string,
  params: { fetch: true; forceFetchBasicData?: boolean; fetchPrice?: boolean; fetchAvailability?: boolean },
): AppThunk<{
  current: ProductDescriptor;
  promise: Promise<ProductDescriptor>;
}>;
function getProductDescriptor(
  productCode: string,
  priceReference: string,
  params: { fetch: false; forceFetchBasicData?: boolean },
): AppThunk<{
  current: ProductDescriptor;
  promise: undefined;
}>;
function getProductDescriptor(
  productCode: string,
  priceReference: string,
  params?: {
    fetch?: boolean;
    forceFetchBasicData?: boolean;
    fetchPrice?: boolean;
    fetchAvailability?: boolean;
  },
): AppThunk<{
  current: ProductDescriptor;
  promise: Promise<ProductDescriptor> | undefined;
}> {
  return (dispatch, getState) => {
    const fetch = params?.fetch ?? false;
    const fetchPrice = params?.fetchPrice ?? true;
    const fetchAvailability = params?.fetchAvailability ?? true;
    const normalizedPriceReference = normalizePriceReference(priceReference);
    const base = getState().products.basic[productCode];

    const promise: Promise<ProductDescriptor> | undefined = fetch
      ? (async () => {
          const ensuredBase = await dispatch(ensureBaseProductData(productCode, params?.forceFetchBasicData));
          const { price, availability } = await loadDescriptorPna(ensuredBase, normalizedPriceReference, {
            fetchPrice,
            fetchAvailability,
          });
          return buildProductDescriptor(
            ensuredBase,
            {
              price: { status: "success", data: price },
              availability: { status: "success", data: availability },
            },
            normalizedPriceReference,
          );
        })()
      : undefined;

    // Synchronous snapshot mirroring the legacy post-dispatch read: a side being fetched shows
    // pending (the legacy `price.load`/`availability.load` dispatches), a skipped or
    // permission-missing side shows the legacy synthesized loaded-empty result, and a missing
    // base without forceFetchBasicData keeps the legacy empty descriptor.
    const descriptorWillLoad = Boolean(base) || Boolean(params?.forceFetchBasicData);
    const effectiveProductCode = pnaRequestProductCode(base, productCode);
    const priceEnabled =
      pnaPriceQueryOptions({
        productCode: effectiveProductCode,
        priceReference: normalizedPriceReference,
        enabled: fetch && descriptorWillLoad && fetchPrice,
      }).enabled === true;
    const availabilityEnabled =
      pnaAvailabilityQueryOptions({
        productCode: effectiveProductCode,
        enabled: fetch && descriptorWillLoad && fetchAvailability,
      }).enabled === true;
    const current = buildProductDescriptor(
      base,
      {
        price: priceEnabled ? { status: "pending" } : { status: "disabled" },
        availability: availabilityEnabled ? { status: "pending" } : { status: "disabled" },
      },
      normalizedPriceReference,
    );

    return { current, promise };
  };
}

interface UseProductDescriptorOptions {
  /**
   * Request data if not present
   */
  fetch?: boolean;
  fetchPrice?: boolean;
  fetchAvailability?: boolean;
  forceFetchBasicData?: boolean;
}

interface UseProductDescriptorProps {
  productCode: string;
  priceReference: string;
  options?: UseProductDescriptorOptions;
}

/** Shared subscription body of the descriptor hooks: stage basic data, subscribe to the canonical PnA queries, project. */
function useSubscribedProductDescriptor(
  { productCode, priceReference, options = {} }: UseProductDescriptorProps,
  { nullable }: { nullable: boolean },
): ProductDescriptor | null {
  const base = useSelector(selectProductData(productCode));
  const dispatch = useDispatch();
  const fetchPrice = options.fetchPrice ?? true;
  const fetchAvailability = options.fetchAvailability ?? true;

  useEffect(() => {
    if (options.fetch && (!nullable || productCode)) {
      dispatch(ensureBaseProductData(productCode, options.forceFetchBasicData));
    }
  }, [dispatch, productCode, priceReference, options.fetch, options.forceFetchBasicData, nullable]);

  const priceOptions = descriptorPriceOptions(base, productCode, normalizePriceReference(priceReference), fetchPrice);
  const availabilityOptions = descriptorAvailabilityOptions(base, productCode, fetchAvailability);
  const priceQuery = useQuery(priceOptions);
  const availabilityQuery = useQuery(availabilityOptions);

  const productDescriptor = useMemo(
    () =>
      projectDescriptorPna(
        base,
        priceQuery,
        availabilityQuery,
        priceReference,
        priceOptions.enabled === true,
        availabilityOptions.enabled === true,
      ),
    [base, priceQuery, availabilityQuery, priceReference, priceOptions, availabilityOptions],
  );

  return nullable && !productCode ? null : productDescriptor;
}

const useNullableProductDescriptor = (props: UseProductDescriptorProps): ProductDescriptor | null => {
  return useSubscribedProductDescriptor(props, { nullable: true });
};

const useProductDescriptor = (props: UseProductDescriptorProps): ProductDescriptor => {
  // `nullable: false` never returns null — the helper's union only carries null for the nullable variant.
  return useSubscribedProductDescriptor(props, { nullable: false })!;
};

interface UseMultipleProductDescriptorsProps {
  productCodes: string[];
  priceReference: string;
  options?: {
    /**
     * Request data if not present
     */
    fetch?: boolean;
    fetchAvailability?: boolean;
  };
}

const useMultipleProductDescriptors = ({
  productCodes,
  priceReference,
  options = {},
}: UseMultipleProductDescriptorsProps) => {
  const bases = useSelector(
    (state: RootState) => productCodes.map((productCode) => state.products.basic[productCode]),
    shallowEqual,
  );
  const dispatch = useDispatch();
  const fetchAvailability = options.fetchAvailability ?? true;
  const normalizedPriceReference = normalizePriceReference(priceReference);
  // Joined key, not array identity: same codes in a new array must not re-trigger dispatches.
  const productCodesKey = productCodes.join("-");

  useEffect(() => {
    if (options.fetch) {
      productCodes.forEach((productCode) => dispatch(ensureBaseProductData(productCode)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, productCodesKey, priceReference, options.fetch]);

  const priceOptionsList = productCodes.map((productCode, index) =>
    descriptorPriceOptions(bases[index], productCode, normalizedPriceReference, true),
  );
  const availabilityOptionsList = productCodes.map((productCode, index) =>
    descriptorAvailabilityOptions(bases[index], productCode, fetchAvailability),
  );
  const priceQueries = useQueries({ queries: priceOptionsList });
  const availabilityQueries = useQueries({ queries: availabilityOptionsList });

  return useMemo(() => {
    const productDescriptors = productCodes.map((_, index) =>
      projectDescriptorPna(
        bases[index],
        priceQueries[index],
        availabilityQueries[index],
        priceReference,
        priceOptionsList[index].enabled === true,
        availabilityOptionsList[index].enabled === true,
      ),
    );
    return toMapFn(
      productDescriptors.filter((_) => _.base),
      (_) => _.base.code,
    );
  }, [
    productCodes,
    bases,
    priceQueries,
    availabilityQueries,
    priceReference,
    priceOptionsList,
    availabilityOptionsList,
  ]);
};

const findPackagingByCode = (productDescriptor: ProductDescriptor, packCode: string) => {
  return (
    productDescriptor.availability?.packagingInformation.find((packaging) => packaging.packCode === packCode) ??
    INVALID_PACKAGING
  );
};

const findOptionalPartById = (productDescriptor: ProductDescriptor, productId: string) => {
  return isKITProduct(productDescriptor.base)
    ? productDescriptor.base.optionalParts.find((item) => item.id === productId)
    : undefined;
};

const EMPTY_WAREHOUSE: WarehouseStockData = {
  code: "",
  name: "-",
  inClarification: 0,
  inProduction: [],
  inProductionWeekly: [],
  inStock: [
    {
      quantity: 0,
    },
  ],
  inStockWeekly: [],
};

const INVALID_PACKAGING: PackagingInformationEntry = {
  packCode: INVALID_PACKAGE_CODE,
  primaryWarehouseCode: "",
  primaryWarehouseName: "",
  warehouses: [EMPTY_WAREHOUSE],
  global: EMPTY_WAREHOUSE,
  totalWarehouse: EMPTY_WAREHOUSE,
  packSize: 1,
  preferredPackQuantity: null,
  replenishmentLeadTimeDate: null,
  minOrderQuantity: 1,
};

export const ProductService = {
  getProductDescriptor,
  useProductDescriptor,
  useNullableProductDescriptor,
  useMultipleProductDescriptors,
  findPackagingByCode,
  findOptionalPartById,
} as const;
